'use client';

import type { Content } from 'mdast';
import type { ChunkDocContext } from '@/lib/parseMarkdown';
import Markdown, { type FootnoteHandler } from '../../Markdown';
import { getPerspectiveTabs, type PerspectiveTab } from '../perspectiveBoxUtils';
import { usePerspectiveSelection } from './usePerspectiveSelection';

interface PerspectiveChoiceContentProps {
  children: Content[];
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler;
}

const getActiveTab = (
  tabs: PerspectiveTab[],
  selected: string,
): PerspectiveTab | undefined => {
  return tabs.find(tab => tab.category === selected)
    ?? tabs.find(tab => tab.category === 'mainScenario')
    ?? tabs[0];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDateMarkerNode = (node: any): boolean =>
  node?.type === 'containerDirective' && node?.name === 'dateMarker';

// Pull every `:::dateMarker` node out of every tab and dedupe by raw text. In
// the source markdown these markers live inside the Main Scenario tab (they
// sit at the end of each chunk and announce the *next* chunk's year). When a
// shorter perspective is active those markers vanish from the DOM, leaving
// the HUD with no way to know which year the reader is in. Rendering them
// once at the end of the chunk wrapper - regardless of active tab - keeps
// the chunk-boundary year transitions in place for every perspective.
const collectChunkDateMarkers = (tabs: PerspectiveTab[]): Content[] => {
  const seen = new Map<string, Content>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fingerprint = (node: any): string => JSON.stringify(node.children ?? []);
  tabs.forEach(tab => {
    tab.content.forEach(node => {
      if (!isDateMarkerNode(node)) return;
      const key = fingerprint(node);
      if (!seen.has(key)) seen.set(key, node);
    });
  });
  return Array.from(seen.values());
};

const stripDateMarkers = (content: Content[]): Content[] =>
  content.filter(node => !isDateMarkerNode(node));

// Renders the active perspective's prose for a :::perspectiveBox. Perspective
// is a page-level mode, so this just renders whichever tab matches the current
// selection — no slide animation, no scroll-anchoring.
export default function PerspectiveChoiceContent(
  {
    children,
    docContext,
    FootnoteHandler,
  }: PerspectiveChoiceContentProps,
) {
  const tabs = getPerspectiveTabs(children);
  const { selected } = usePerspectiveSelection();

  if (tabs.length === 0) return null;

  const activeTab = getActiveTab(tabs, selected);
  const chunkDateMarkers = collectChunkDateMarkers(tabs);

  // dateMarkers are hoisted to a chunk-level sibling below so the HUD's
  // scroll-driven year clock works in every perspective, not just whichever
  // tab the markers originally lived in.
  const activeTabContent = activeTab ? stripDateMarkers(activeTab.content) : [];

  return (
    <div className="perspective-choice mt-4 overflow-x-clip">
      {activeTab && (
        <div
          key={activeTab.category}
          className="markdown-content [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
        >
          <Markdown
            nodes={activeTabContent}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        </div>
      )}
      {/* Render the chunk's dateMarker(s) outside the per-tab content so the
          HUD's scroll-driven year transitions work in every perspective, not
          just whichever tab the markers originally lived in. */}
      {chunkDateMarkers.length > 0 && (
        <Markdown
          nodes={chunkDateMarkers}
          docContext={docContext}
          FootnoteHandler={FootnoteHandler}
        />
      )}
    </div>
  );
}
